import { useCallback, useState, useEffect, useMemo } from "react";
import { useRealtimeTables } from "./useRealtimeTables";
import { globalEventBus } from "@/lib/realtime/EventBus.js";
import { initialTables } from "@/mocks/initialTables.js";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

/**
 * Hook for managing game tables
 * Integrates with WebSocket when available, falls back to local state
 * @returns {Object} Table state and methods
 */
export const useTables = () => {
  const { t } = useTranslation();
  // Try to use realtime tables (WebSocket)
  const realtimeTables = useRealtimeTables();

  // Performance marks (dev only)
  // performance.mark("useTables-start");

  // Helper para criar novo objeto de mesa (evita recriação inline)
  const createNewTable = useCallback(
    (tableData, creatorName) => ({
      id: `ws_${Date.now()}`,
      name: tableData.name || "Nova Mesa",
      owner: creatorName,
      isPrivate: tableData.isPrivate || false,
      password: tableData.password || null,
      players: [creatorName],
      spectators: [],
      maxPlayers: tableData.maxPlayers || 6,
      allowSpectators: tableData.allowSpectators !== false,
      gameType: "Guild for Glory",
      createdAt: new Date().toISOString(),
      status: "draft",
      creator: creatorName,
    }),
    []
  );

  // Local fallback state - Comprehensive mock scenarios
  const [localTables, setLocalTables] = useState(() => initialTables);

  // Memoized merge of realtime tables with local placeholders (draft/publishing/needsSync)
  // When connected to Colyseus, use ONLY Colyseus tables to avoid ID conflicts
  const activeTables = useMemo(() => {
    const rt = Array.isArray(realtimeTables.tables)
      ? realtimeTables.tables
      : [];
    const local = Array.isArray(localTables) ? localTables : [];

    // If connected to Colyseus, only use Colyseus tables + local drafts
    if (realtimeTables.isAvailable && rt.length > 0) {
      const map = new Map();
      rt.forEach((t) => map.set(t.id, t));

      // Only add local draft tables that don't exist in Colyseus
      local.forEach((t) => {
        if (t.status === "draft" && !map.has(t.id)) {
          map.set(t.id, t);
        }
      });

      return Array.from(map.values());
    }

    // Fallback to local tables when not connected

    return local;
  }, [realtimeTables.tables, realtimeTables.isAvailable, localTables]);

  // Load persisted draft tables (local-only fallback) on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem("gg_draft_tables_v1");
      if (!raw) return;
      const persisted = JSON.parse(raw);
      if (Array.isArray(persisted) && persisted.length > 0) {
        setLocalTables((prev) => {
          const existingIds = new Set(prev.map((t) => t.id));
          const toAdd = persisted.filter(
            (t) => t && t.status === "draft" && !existingIds.has(t.id)
          );
          if (toAdd.length === 0) return prev;
          return [...prev, ...toAdd];
        });
      }
    } catch (err) {
      console.warn("[useTables] Failed to load persisted draft tables", err);
    }
  }, []);

  // Helper to persist draft tables only
  const persistDraftTables = useCallback((tablesSnapshot) => {
    try {
      const drafts = tablesSnapshot.filter((t) => t.status === "draft");
      localStorage.setItem("gg_draft_tables_v1", JSON.stringify(drafts));
    } catch (err) {
      console.warn("[useTables] Failed to persist draft tables", err);
    }
  }, []);

  /**
   * Create a new table with WebSocket support
   * @param {Object} tableData - The table data
   * @param {Function} onCreated - Callback quando table for criada (recebe tableId)
   * @returns {Object} {success, isWebSocket} - The result object
   */
  const createTable = useCallback(
    (tableData, onCreated) => {
      if (import.meta?.env?.DEV) {
        console.warn(t("useTables.create_called"), {
          tableData,
          hasCallback: !!onCreated,
          callbackType: typeof onCreated,
        });
      }
      // Use WebSocket if available (emit function present)
      if (realtimeTables.isAvailable) {
        try {
          // Register listener for server confirmation with specific tableCreatedSuccess
          if (onCreated && typeof onCreated === "function") {
            if (import.meta?.env?.DEV) {
              console.warn(t("useTables.registering_eventbus"));
            }
            // Listen for tableCreatedSuccess (sent only to creator) with timeout
            const timeoutId = setTimeout(() => {
              console.warn(
                "[useTables] Timeout waiting for tableCreatedSuccess"
              );
            }, 5000);

            globalEventBus.once("tableCreatedSuccess", ({ table }) => {
              clearTimeout(timeoutId);
              if (import.meta?.env?.DEV) {
                console.warn(
                  "[useTables] tableCreatedSuccess received:",
                  table
                );
              }
              if (table && table.id) {
                if (import.meta?.env?.DEV) {
                  console.warn(t("useTables.calling_onCreated"), table.id);
                }
                onCreated(table.id);
              }
            });
          } else {
            console.warn(t("useTables.no_valid_callback"));
          }

          // Emit WebSocket event to create a table on the server
          const emitResult = realtimeTables.createTable({
            name: tableData.name || "Nova Mesa",
            isPrivate: tableData.isPrivate || false,
            password: tableData.password || null,
            maxPlayers: tableData.maxPlayers || 6,
            allowSpectators: tableData.allowSpectators !== false,
            gameType: "Guild for Glory",
            creator: tableData.creator || "Anonymous",
            status: "waiting",
            createdAt: tableData.createdAt,
          });
          // Se emitResult for falsy, cria draft local com id ws_<timestamp>
          if (!emitResult) {
            const creatorName = tableData.creator || "Anonymous";
            const newTable = createNewTable(tableData, creatorName);
            setLocalTables((prev) => [...prev, newTable]);
            if (onCreated && typeof onCreated === "function") {
              onCreated(newTable.id);
            }
            return {
              success: true,
              isWebSocket: false,
              isDraft: true,
              message: t("useTables.draft_created_success"),
            };
          }

          return {
            success: true,
            isWebSocket: true,
          };
        } catch (error) {
          toast.error(t("useTables.create_failed"));
          return { success: false, isWebSocket: false, error: error.message };
        }
      }

      // Fallback: Create local draft table
      const creatorName = tableData.creator || "Anonymous";
      const newTable = createNewTable(tableData, creatorName);
      newTable.needsSync = true;

      setLocalTables((prev) => {
        const next = [...prev, newTable];
        persistDraftTables(next);
        return next;
      });

      // Call callback immediately for local table
      if (onCreated && typeof onCreated === "function") {
        onCreated(newTable.id);
      }

      // Return success object
      return {
        success: true,
        isWebSocket: false,
        isDraft: true,
        message: t("useTables.draft_created_success"),
      };
    },
    [realtimeTables, persistDraftTables, createNewTable, t]
  );

  /**
   * Cancel draft table (local only)
   * WebSocket tables are managed by the server
   */
  const cancelDraftTable = useCallback(
    (tableId) => {
      setLocalTables((prev) => {
        const next = prev.filter(
          (table) => !(table.id === tableId && table.status === "draft")
        );
        persistDraftTables(next);
        return next;
      });
    },
    [persistDraftTables]
  );

  /**
   * Publish a local draft table (makes it visible as a normal waiting table)
   * If realtime backend is available, emits creation immediately and removes local draft.
   * Otherwise marks for future sync when backend becomes available.
   * @param {number} tableId - Draft table id
   * @returns {{success: boolean, table: Object|undefined, error: string|undefined}}
   */
  const publishDraftTable = useCallback(
    (tableId) => {
      // Locate local draft
      const draft = localTables.find(
        (t) => t && t.id === tableId && t.status === "draft"
      );
      if (!draft) {
        toast.error(t("useTables.publish_unavailable"));
        return { success: false, error: t("useTables.draft_not_found") };
      }

      // If realtime available: mark as publishing placeholder and emit
      if (realtimeTables.isAvailable) {
        // Mark local as publishing to keep visible until server echoes
        setLocalTables((prev) => {
          const next = prev.map((it) =>
            it.id === tableId
              ? { ...it, status: "waiting", publishing: true, needsSync: false }
              : it
          );
          persistDraftTables(next);
          return next;
        });

        // Once server confirms creation, remove local placeholder
        globalEventBus.once("tableCreated", () => {
          const matches = (t) =>
            (t.owner || t.creator || "") ===
              (draft.owner || draft.creator || "") && t.name === draft.name;
          setLocalTables((prev) => {
            const next = prev.filter((t) => !matches(t));
            persistDraftTables(next);
            return next;
          });
        });

        try {
          realtimeTables.createTable({
            name: draft.name,
            isPrivate: draft.isPrivate,
            password: draft.password,
            maxPlayers: draft.maxPlayers,
            allowSpectators: draft.allowSpectators,
            gameType: draft.gameType,
            creator: draft.owner || draft.creator || "Anonymous",
            status: "waiting",
            createdAt: draft.createdAt, // Preserve original creation timestamp
          });
          toast.success(t("useTables.publish_success"));
          return {
            success: true,
            table: { ...draft, status: "waiting" },
            message: t("useTables.publish_success"),
          };
        } catch (err) {
          console.warn(t("useTables.publish_failed"), err);
          toast.error(t("useTables.publish_unavailable"));
          return { success: false, error: t("useTables.emit_failed") };
        }
      }

      // Offline: mark for future sync
      setLocalTables((prev) => {
        const next = prev.map((t) =>
          t.id === tableId ? { ...t, status: "waiting", needsSync: true } : t
        );
        persistDraftTables(next);
        return next;
      });
      toast.info(
        t("useTables.publish_success") +
          " " +
          t("useTables.publish_pending_sync")
      );
      return {
        success: true,
        table: { ...draft, status: "waiting" },
        message: t("useTables.publish_pending_sync"),
      };
    },
    [localTables, persistDraftTables, realtimeTables, t]
  );

  /**
   * Start a game with WebSocket support
   * Transitions status from waiting/draft to playing
   * @param {string} tableId - The table ID
   * @param {Object} currentUser - Current user object
   * @returns {Object} {success, table/error}
   */
  const startGame = useCallback(
    (tableId, currentUser) => {
      const table = activeTables.find((t) => t.id === tableId);
      if (!table) {
        toast.error(t("useTables.table_not_found"));
        return {
          success: false,
          error: t("useTables.table_not_found"),
        };
      }
      if (table.creator !== currentUser.username) {
        toast.error(t("useTables.only_creator_can_start"));
        return {
          success: false,
          error: t("useTables.only_creator_can_start"),
        };
      }

      // Use WebSocket if available
      if (realtimeTables.startGame) {
        try {
          realtimeTables.startGame(tableId);
          return {
            success: true,
            table: { ...table, status: "playing" },
            message: t("useTables.game_started_success"),
          };
        } catch (error) {
          console.error("Error starting game via WebSocket:", error);
          // Fall through to local state
        }
      }

      // Fallback to local state
      setLocalTables((prev) => {
        const next = prev.map((table) => {
          if (table.id === tableId) {
            return {
              ...table,
              status: "playing",
            };
          }
          return table;
        });
        // Remove non-drafts from persistence
        persistDraftTables(next);
        return next;
      });

      return {
        success: true,
        table: { ...table, status: "playing" },
        message: t("useTables.game_started_success"),
      };
    },
    [activeTables, realtimeTables, persistDraftTables, t]
  );

  /**
   * Join a table with WebSocket support
   * Supports mid-game joins for playing/in_progress status
   * @param {string} tableId - The table ID
   * @param {Object} user - User object
   * @param {string} mode - "player" or "spectator"
   * @returns {Object} {success, table/error, requiresLobby}
   */
  const joinTable = useCallback(
    (tableId, user, mode = "player") => {
      const table = activeTables.find((t) => t.id === tableId);
      if (!table) {
        toast.error(t("useTables.table_not_found"));
        return {
          success: false,
          error: t("useTables.table_not_found"),
        };
      }

      // Check if user is already in the table
      const playersList = Array.isArray(table.players)
        ? table.players.map((p) => (typeof p === "string" ? p : p.username))
        : [];
      const spectatorsList = Array.isArray(table.spectators)
        ? table.spectators.map((s) => (typeof s === "string" ? s : s.username))
        : [];

      if (
        playersList.includes(user.username) ||
        spectatorsList.includes(user.username)
      ) {
        toast.error(t("useTables.already_in_table"));
        return {
          success: false,
          error: t("useTables.already_in_table"),
        };
      }

      const isPlayer = mode === "player";

      // Check capacity
      if (isPlayer && playersList.length >= table.maxPlayers) {
        toast.error(t("useTables.table_full"));
        return {
          success: false,
          error: t("useTables.table_full"),
        };
      }
      if (!isPlayer && !table.allowSpectators) {
        toast.error(t("useTables.spectators_not_allowed"));
        return {
          success: false,
          error: t("useTables.spectators_not_allowed"),
        };
      }

      // Check if mid-game join (requires lobby for character selection)
      const requiresLobby = table.status === "in_progress" && isPlayer;

      // Use WebSocket if available
      if (realtimeTables.joinGameTable) {
        try {
          realtimeTables.joinGameTable(tableId);
          return {
            success: true,
            table: { ...table, status: table.status },
            requiresLobby,
            message: t("useTables.join_success"),
          };
        } catch (error) {
          console.error("Error joining table via WebSocket:", error);
          // Fall through to local state
        }
      }

      // Fallback to local state
      setLocalTables((prev) => {
        const next = prev.map((t) => {
          if (t.id === tableId) {
            const targetArray = isPlayer ? "players" : "spectators";
            return {
              ...t,
              [targetArray]: [...t[targetArray], user.username],
            };
          }
          return t;
        });
        persistDraftTables(next);
        return next;
      });

      return {
        success: true,
        table: { ...table, status: table.status },
        requiresLobby,
        message: t("useTables.join_success"),
      };
    },
    [activeTables, realtimeTables, persistDraftTables, t]
  );

  /**
   * Leave a table with WebSocket support
   */
  const leaveTable = useCallback(
    (tableId, username) => {
      // Use WebSocket if available
      if (realtimeTables.leaveGameTable) {
        try {
          realtimeTables.leaveGameTable(tableId);
          return;
        } catch (error) {
          console.error("Error leaving table via WebSocket:", error);
          // Fall through to local state
        }
      }

      // Fallback to local state
      setLocalTables((prev) => {
        const next = prev.map((table) => {
          if (table.id === tableId) {
            return {
              ...table,
              players: table.players.filter((p) => p !== username),
              spectators: table.spectators.filter((s) => s !== username),
            };
          }
          return table;
        });
        persistDraftTables(next);
        return next;
      });
      toast.info(t("useTables.leave_success"));
    },
    [realtimeTables, persistDraftTables, t]
  );

  /**
   * Spectate a table (alias for joinTable with mode="spectator")
   */
  const spectateTable = useCallback(
    (tableId, user) => {
      toast.info(t("useTables.spectate_success"));
      return joinTable(tableId, user, "spectator");
    },
    [joinTable, t]
  );

  /**
   * Return to an existing table the user is already part of
   * @param {string} tableId - The table ID
   * @param {Object} user - User object
   * @returns {Object} {success, viewName, table/error}
   */
  const returnToTable = useCallback(
    (tableId, user) => {
      const table = activeTables.find((t) => t.id === tableId);
      if (!table) {
        toast.error(t("useTables.table_not_found"));
        return {
          success: false,
          error: t("useTables.table_not_found"),
        };
      }

      // Handle both string and object formats for players/spectators
      const playersList = Array.isArray(table.players)
        ? table.players.map((p) => (typeof p === "string" ? p : p.username))
        : [];
      const spectatorsList = Array.isArray(table.spectators)
        ? table.spectators.map((s) => (typeof s === "string" ? s : s.username))
        : [];

      if (
        !playersList.includes(user.username) &&
        !spectatorsList.includes(user.username)
      ) {
        toast.error(t("useTables.not_in_table"));
        return {
          success: false,
          error: t("useTables.not_in_table"),
        };
      }

      // Determine view: lobby for waiting/draft, game for playing/in_progress/paused
      const viewName =
        table.status === "playing" ||
        table.status === "in_progress" ||
        table.status === "paused"
          ? "game"
          : "lobby";

      return {
        success: true,
        viewName,
        table,
        message: t("useTables.return_success"),
      };
    },
    [activeTables, t]
  );

  /**
   * Get tables by status (helper function)
   * @param {string} status - Status to filter by
   * @returns {Array} Filtered tables
   */
  const getTablesByStatus = useCallback(
    (status) => {
      return activeTables.filter((t) => t.status === status);
    },
    [activeTables]
  );

  /**
   * Check if a table allows mid-game joins
   * @param {Object} table - Table object
   * @returns {boolean}
   */
  const canJoinMidGame = useCallback((table) => {
    return (
      table.status === "playing" ||
      table.status === "in_progress" ||
      table.status === "paused"
    );
  }, []);

  // Auto-sync effect: when realtime becomes available, emit creation for offline tables needing sync
  useEffect(() => {
    if (!realtimeTables.isAvailable) return;
    setLocalTables((prev) => {
      const toSync = prev.filter((t) => t.needsSync);
      if (toSync.length === 0) return prev;
      const syncedIds = new Set();
      toSync.forEach((t) => {
        try {
          realtimeTables.createTable({
            name: t.name,
            isPrivate: t.isPrivate,
            password: t.password,
            maxPlayers: t.maxPlayers,
            allowSpectators: t.allowSpectators,
            gameType: t.gameType,
            creator: t.owner || t.creator || "Anonymous",
            status: t.status === "draft" ? "waiting" : t.status,
          });
          syncedIds.add(t.id);
        } catch (err) {
          console.warn("[useTables] Failed to sync table", t.id, err);
        }
      });
      const next = prev.map((t) =>
        syncedIds.has(t.id) ? { ...t, needsSync: false } : t
      );
      persistDraftTables(next);
      if (syncedIds.size > 0) {
        toast.success(t("useTables.sync_success", { count: syncedIds.size }));
      }
      return next;
    });
  }, [realtimeTables.isAvailable, persistDraftTables, realtimeTables, t]);

  return {
    activeTables,
    createTable,
    cancelDraftTable,
    publishDraftTable,
    startGame,
    joinTable,
    leaveTable,
    spectateTable,
    returnToTable,
    getTablesByStatus,
    canJoinMidGame,
    // Expose helper from useRealtimeTables
    byId: realtimeTables.byId,
    byStatus: realtimeTables.byStatus,
  };
};
